import { ConfigProvider, DatePicker, TimeRangePickerProps } from "antd";
import { Calendar } from "lucide-react";
import { DateTime } from "luxon";
import luxonGenerateConfig from "rc-picker/lib/generate/luxon";
import React, { useEffect, useMemo, useState } from "react";

import { IgButton } from "@intelligems/ig-theme";
import { ensureNumber } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import { useDispatch, useSelector } from "@src/store";
import { theme } from "@src/theme";

import { useGetOrg } from "../../../api/organizations/get-organization";
import { updateSitewideDashboardFilter } from "../../../store/slices/sitewideDashboardFilters";

const LuxonDatePicker =
  DatePicker.generatePicker<DateTime>(luxonGenerateConfig);
const { RangePicker } = LuxonDatePicker;
const datePickerTheme = {
  token: {
    colorPrimary: theme.palette.primary.dark,
  },
  components: {
    DatePicker: {
      controlItemBgActive: theme.igBorders.core.disabled,
    },
  },
};

export const SitewideDateRangePicker = () => {
  const dispatch = useDispatch();
  const sitewideDashboardFilters = useSelector(
    (state) => state.sitewideDashboardFilters
  );
  const org = useGetOrg();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [buttonClicked, setButtonClicked] = useState<
    "initialState" | "cancel" | "ok" | "dateSelector" | undefined
  >("initialState");
  const [onOpenChange, setOnOpenChange] = useState<boolean | undefined>(
    undefined
  );
  const [processUpdate, setProcessUpdate] = useState<number>(0);

  const [tempStartTime, setTempStartTime] = useState<number | null>(
    sitewideDashboardFilters.startTime
      ? ensureNumber(sitewideDashboardFilters.startTime)
      : null
  );
  const [tempEndTime, setTempEndTime] = useState<number | null>(
    sitewideDashboardFilters.endTime
      ? ensureNumber(sitewideDashboardFilters.endTime)
      : null
  );

  const resetTempSelections = () => {
    setTempStartTime(
      sitewideDashboardFilters.startTime
        ? ensureNumber(sitewideDashboardFilters.startTime)
        : null
    );
    setTempEndTime(
      sitewideDashboardFilters.endTime
        ? ensureNumber(sitewideDashboardFilters.endTime)
        : null
    );
  };

  const setSelections = () => {
    dispatch(
      updateSitewideDashboardFilter({
        key: "startTime",
        value: tempStartTime ? tempStartTime - 1 : tempStartTime, // removing +1 ms added
      })
    );
    dispatch(
      updateSitewideDashboardFilter({
        key: "endTime",
        value: tempEndTime ? tempEndTime - 1 : tempEndTime, // removing +1 ms added
      })
    );
  };

  useEffect(() => {
    setTempStartTime(
      sitewideDashboardFilters.startTime
        ? ensureNumber(sitewideDashboardFilters.startTime)
        : null
    );
    setTempEndTime(
      sitewideDashboardFilters.endTime
        ? ensureNumber(sitewideDashboardFilters.endTime)
        : null
    );
  }, [sitewideDashboardFilters.startTime, sitewideDashboardFilters.endTime]);

  useEffect(() => {
    if (onOpenChange && !isOpen) {
      // Handles initial opening of calendar selector
      setIsOpen(true);
      setOnOpenChange(false);
    } else if (buttonClicked === "ok" || buttonClicked === "cancel") {
      // Handling Ok and Cancel Clicked
      setIsOpen(false);
      setButtonClicked(undefined);
      setOnOpenChange(undefined);

      if (buttonClicked === "ok") {
        setSelections();
      } else {
        resetTempSelections();
      }
    } else if (buttonClicked === "dateSelector") {
      // Handles date selection being made
      setButtonClicked(undefined);
      setOnOpenChange(false);
    } else if (!onOpenChange) {
      // handles clicking out of the calendar
      resetTempSelections();
      setButtonClicked(undefined);
      setIsOpen(false);
    }
  }, [processUpdate]);

  const timezone = org?.data?.org?.timezone ?? "UTC";

  const rangePresets = useMemo<TimeRangePickerProps["presets"]>(
    () => [
      {
        label: "Today",
        value: [
          // @ts-ignore
          DateTime.now().setZone(timezone).startOf("day"),
          // @ts-ignore
          DateTime.now().setZone(timezone),
        ],
      },
      {
        label: "Yesterday",
        value: [
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ days: 1 }).startOf("day"),
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ days: 1 }).endOf("day"),
        ],
      },
      {
        label: "Last 7 Days",
        value: [
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ days: 7 }).startOf("day"),
          // @ts-ignore
          DateTime.now().setZone(timezone),
        ],
      },
      {
        label: "Last 30 Days",
        value: [
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ days: 30 }).startOf("day"),
          // @ts-ignore
          DateTime.now().setZone(timezone),
        ],
      },
      {
        label: "Last 90 Days",
        value: [
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ days: 90 }).startOf("day"),
          // @ts-ignore
          DateTime.now().setZone(timezone),
        ],
      },
      {
        label: "Last 6 Months",
        value: [
          // @ts-ignore
          DateTime.now()
            .setZone(timezone)
            .minus({ months: 6 })
            .startOf("month"),
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ months: 1 }).endOf("month"),
        ],
      },
      {
        label: "Last 12 Months",
        value: [
          // @ts-ignore
          DateTime.now()
            .setZone(timezone)
            .minus({ months: 12 })
            .startOf("month"),
          // @ts-ignore
          DateTime.now().setZone(timezone).minus({ months: 1 }).endOf("month"),
        ],
      },
    ],
    [timezone]
  );

  return (
    <ConfigProvider theme={datePickerTheme}>
      <RangePicker
        timezone={timezone}
        open={isOpen}
        onOpenChange={(e) => {
          setOnOpenChange(e);
          setProcessUpdate(processUpdate + 1);
        }}
        value={[
          tempStartTime
            ? DateTime.fromMillis(ensureNumber(tempStartTime))
            : null,
          tempEndTime ? DateTime.fromMillis(ensureNumber(tempEndTime)) : null,
        ]}
        onChange={(e) => {
          // The + 1 on startTime/endTime is very important, do not remove.
          // It solves an issue where if you click a date selection (but it does not change the date) think
          // double click 2 weeks selection, the date selector closes
          const startTime = e ? e[0]!.toMillis() + 1 : null;
          const endTime = e ? e[1]!.toMillis() + 1 : null;

          setTempStartTime(startTime);
          setTempEndTime(endTime);
          setButtonClicked("dateSelector");
          setProcessUpdate(processUpdate + 1);
        }}
        style={{
          height: "36px",
          borderRadius: "6px",
          boxShadow: "none",
          border: "none",
        }}
        // @ts-ignore
        presets={rangePresets}
        placement="bottomLeft"
        format="MMM DD, YYYY"
        allowClear={false}
        bordered={true}
        popupClassName="date-picker-dropdown"
        suffixIcon={null}
        renderExtraFooter={() => (
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            sx={{
              padding: "8px 12px",
            }}
          >
            <Grid item>
              <IgButton
                size="small"
                color="secondary"
                onClick={() => {
                  setButtonClicked("cancel");
                  setProcessUpdate(processUpdate + 1);
                }}
              >
                Cancel
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                variant="contained"
                size="small"
                onClick={() => {
                  setButtonClicked("ok");
                  setProcessUpdate(processUpdate + 1);
                }}
              >
                Ok
              </IgButton>
            </Grid>
          </Grid>
        )}
      />
    </ConfigProvider>
  );
};
